import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, ContentCopy, Email, Download } from '@mui/icons-material';
import QRCode from 'qrcode';
import axiosInstance from '../../utils/axiosConfig';
import { trackEvent } from '../../utils/analytics';

interface BandQRModalProps {
  open: boolean;
  onClose: () => void;
  bandId: string;
  bandName: string;
  bandSlug?: string;
}

const BandQRModal: React.FC<BandQRModalProps> = ({ open, onClose, bandId, bandName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [qrCodeData, setQrCodeData] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [selectedView, setSelectedView] = useState<'public' | 'strand' | 'admin'>('strand');
  const [emailData, setEmailData] = useState({
    to: '',
    subject: `Check out this artist: ${bandName}`,
    message: `Hi,\n\nI wanted to share this artist with you: ${bandName}\n\nYou can view their full profile, media, and availability at:\n${window.location.origin}/band/${bandId}\n\nBest regards`,
  });
  const [emailSending, setEmailSending] = useState(false);

  // /go/band/:id routes through the server-side QR scan tracker before redirecting
  const bandUrl = selectedView !== 'admin'
    ? `${window.location.origin}/go/band/${bandId}?view=${selectedView}`
    : `${window.location.origin}/go/band/${bandId}`;

  useEffect(() => {
    if (!open || !bandId) return;
    QRCode.toDataURL(bandUrl, {
      width: 400,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then(data => {
        setQrCodeData(data);
        trackEvent('qr_generated', { bandId, bandName, viewType: selectedView });
      })
      .catch(console.error);
  }, [open, bandId, bandUrl]);

  useEffect(() => {
    if (!open) {
      setShowEmailForm(false);
      setCopySuccess(false);
      setSelectedView('strand');
      setEmailData({
        to: '',
        subject: `Check out this artist: ${bandName}`,
        message: `Hi,\n\nI wanted to share this artist with you: ${bandName}\n\nYou can view their full profile, media, and availability at:\n${window.location.origin}/band/${bandId}\n\nBest regards`,
      });
    }
  }, [open, bandId, bandName]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(bandUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = qrCodeData;
    link.download = `${bandName.replace(/[^a-z0-9]/gi, '_')}_QR.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendEmail = async () => {
    if (!emailData.to.trim()) return;
    setEmailSending(true);
    try {
      await axiosInstance.post('/api/bandrecommendations/send-email', {
        to: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
        bandId,
        bandName,
        bandUrl,
        qrCodeData,
      });
      trackEvent('email_sent', { bandId, bandName });
      alert('Email sent successfully!');
      setShowEmailForm(false);
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleClose = () => {
    setShowEmailForm(false);
    onClose();
  };

  // Explicit colours that work in both light and dark themes
  const urlBoxSx = {
    display: 'block',
    bgcolor: 'rgba(0,0,0,0.45)',
    color: '#ffffff',
    border: '1px solid rgba(255,255,255,0.15)',
    p: 1.25,
    borderRadius: 1,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    mb: 2,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {showEmailForm ? 'Email Artist Profile' : `QR Code — ${bandName}`}
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
        {!showEmailForm ? (
          <>
            {/* View selector */}
            <FormControl fullWidth size="small" sx={{ mb: 2, flexShrink: 0, mt: 1 }}>
              <InputLabel>Share As</InputLabel>
              <Select
                value={selectedView}
                onChange={(e) => setSelectedView(e.target.value as 'public' | 'strand' | 'admin')}
                label="Share As"
              >
                <MenuItem value="public">🌍 Public View — general sharing</MenuItem>
                <MenuItem value="strand">🎭 Programmer View — contact, specs &amp; fees</MenuItem>
                <MenuItem value="admin">🔒 Full Access — requires login</MenuItem>
              </Select>
            </FormControl>

            {/* On mobile: QR left, URL + actions right. On desktop: QR centred, full-width below. */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              gap: 2,
              alignItems: { xs: 'flex-start', sm: 'center' },
              flex: 1,
            }}>
              {/* QR code */}
              {qrCodeData && (
                <Box
                  component="img"
                  src={qrCodeData}
                  alt={`QR code for ${bandName}`}
                  sx={{
                    display: 'block',
                    flexShrink: 0,
                    width: { xs: 'min(150px, 36vw)', sm: 260 },
                    height: 'auto',
                    borderRadius: 1,
                  }}
                />
              )}

              {/* URL box + action buttons */}
              <Box sx={{ flex: 1, width: { sm: '100%' } }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#ffffff', fontWeight: 600, letterSpacing: '0.04em' }}>
                  Scan or share this link:
                </Typography>

                <Typography variant="caption" sx={urlBoxSx}>
                  {bandUrl}
                </Typography>

                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                }}>
                  <Button
                    onClick={copyToClipboard}
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy fontSize="small" />}
                    fullWidth
                  >
                    {copySuccess ? 'Copied!' : 'Copy URL'}
                  </Button>
                  <Button
                    onClick={handleDownload}
                    variant="outlined"
                    size="small"
                    startIcon={<Download fontSize="small" />}
                    fullWidth
                  >
                    Download QR
                  </Button>
                  <Button
                    onClick={() => setShowEmailForm(true)}
                    variant="contained"
                    size="small"
                    startIcon={<Email fontSize="small" />}
                    fullWidth
                  >
                    Email
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <TextField
              fullWidth label="To (Email Address)" type="email"
              value={emailData.to}
              onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
              sx={{ mb: 2 }} required autoFocus
            />
            <TextField
              fullWidth label="Subject"
              value={emailData.subject}
              onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth label="Message" multiline rows={6}
              value={emailData.message}
              onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              The email will include the QR code and a direct link to the artist profile.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        {showEmailForm ? (
          <>
            <Button onClick={() => setShowEmailForm(false)}>Back</Button>
            <Button
              onClick={handleSendEmail}
              variant="contained"
              disabled={!emailData.to.trim() || emailSending}
              startIcon={<Email />}
            >
              {emailSending ? 'Sending…' : 'Send Email'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained" fullWidth={isMobile}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BandQRModal;
